/**
 * Super Admin Dashboard
 * Platform overview with statistics and quick actions
 */

import { useNavigate } from 'react-router-dom';
import { useDashboardStats } from '../hooks/useDashboardStats';
import {
  StatCard,
  StatIcons,
  DashboardGrid,
  LoadingState,
  ErrorState,
} from '../components/dashboard';
import { NavCard, PageHeader } from '../components/common';
import { COLORS, SPACING, TYPOGRAPHY } from '../constants/theme';

export function Dashboard() {
  const { stats, loading, error, refreshStats } = useDashboardStats();
  const navigate = useNavigate();

  if (loading) {
    return <LoadingState message="Loading dashboard..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load dashboard"
        message={error}
        onRetry={refreshStats}
      />
    );
  }

  return (
    <div style={{ padding: SPACING['2xl'] }}>
      <PageHeader title="Platform Overview" subtitle="Monitor your entire platform at a glance" />

      {/* Stats Grid */}
      <DashboardGrid columns={3}>
        <StatCard
          title="Total Companies"
          value={stats.totalCompanies}
          icon={StatIcons.companies}
          color={COLORS.primary.light}
          iconColor={COLORS.primary.dark}
          subtitle="Registered organizations"
          onClick={() => navigate('/companies')}
        />
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={StatIcons.users}
          color={COLORS.success.light}
          iconColor={COLORS.success.dark}
          subtitle="Company admins, managers & staff"
        />
        <StatCard
          title="Active One-Time Jobs"
          value={stats.activeOneTimeJobs}
          icon={StatIcons.clipboard}
          color={COLORS.warning.light}
          iconColor={COLORS.warning.dark}
          subtitle="Scheduled and in-progress"
        />
      </DashboardGrid>

      {/* Quick Links */}
      <div style={{ marginTop: SPACING['3xl'] }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING.lg }}>
          <h2 style={{ fontSize: TYPOGRAPHY.fontSize.xl, fontWeight: TYPOGRAPHY.fontWeight.semibold, color: COLORS.text.primary, margin: 0 }}>
            Quick Links
          </h2>
          <button
            onClick={refreshStats}
            title="Refresh data"
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px',
              background: 'none',
              border: `1px solid ${COLORS.border.light}`,
              borderRadius: '6px',
              fontSize: TYPOGRAPHY.fontSize.sm,
              color: COLORS.text.secondary,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLORS.primary.main; e.currentTarget.style.color = COLORS.primary.main; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border.light; e.currentTarget.style.color = COLORS.text.secondary; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            Refresh
          </button>
        </div>
        <div style={{ display: 'flex', gap: SPACING.md, flexWrap: 'wrap' }}>
          <NavCard
            label="Companies"
            description="Manage all registered organizations"
            icon={StatIcons.companies}
            onClick={() => navigate('/companies')}
          />
        </div>
      </div>
    </div>
  );
}
